import { create, enforce, omitWhen, test } from 'vest';

import { CANADA } from '../../src/Steps/ContactDetailsStep/consts';
import { isBlank } from '../../src/utils/isBlank';
import { LeadCaptureFormFields, LeadFormValues } from '../types/types';

export const isEmail = (value: string) => {
  if (isBlank(value)) {
    return true;
  }
  if (value.length > 255) {
    return false;
  }
  const emailRegex =
    // eslint-disable-next-line no-control-regex, no-useless-escape
    /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
  return emailRegex.test(value);
};

export const isPhoneNumber = (value: string) => {
  if (isBlank(value)) {
    return false;
  }
  const phoneRegex =
    /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;
  return phoneRegex.test(value);
};

export const usZipCodeRegExp = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
export const caZipCodeRegExp = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

const CHARACTER_LIMIT = 100;
const EMAIL_CHARACTER_LIMIT = 100;
const ADDRESS_LINE_1_CHARACTER_LIMIT = 100;
const ADDRESS_LINE_2_CHARACTER_LIMIT = 50;
const CITY_CHARACTER_LIMIT = 50;

const suite = create((values: LeadFormValues, formFields?: Partial<LeadCaptureFormFields>) => {
  test('capture_first_name', () => {
    enforce(values.capture_first_name).message('First name is required').isNotEmpty().isNotBlank();
    enforce(values.capture_first_name.length)
      .message(`Max character limit: ${CHARACTER_LIMIT}`)
      .lessThan(CHARACTER_LIMIT);
  });

  test('capture_phone_number', () => {
    enforce(values.capture_phone_number).message('Phone number is required').isNotEmpty().isNotBlank();
    enforce(values.capture_phone_number)
      .message('Phone number is not valid')
      .condition((value) => isPhoneNumber(value));
  });

  omitWhen(!formFields?.capture_last_name, () => {
    test('capture_last_name', () => {
      enforce(values.capture_last_name).message('Last name is required').isNotEmpty().isNotBlank();
      enforce(values.capture_last_name.length)
        .message(`Max character limit: ${CHARACTER_LIMIT}`)
        .lessThan(CHARACTER_LIMIT);
    });
  });

  omitWhen(!formFields?.capture_email, () => {
    test('capture_email', () => {
      enforce(values.capture_email).message('E-mail address is required').isNotEmpty().isNotBlank();
      enforce(values.capture_email?.length)
        .message(`Max character limit: ${EMAIL_CHARACTER_LIMIT}`)
        .lessThan(EMAIL_CHARACTER_LIMIT);
      enforce(values.capture_email)
        .message('E-mail address is not valid')
        .isNotBlank()
        .condition((value) => {
          return isEmail(value);
        });
    });
  });

  omitWhen(!formFields?.capture_address, () => {
    test('country', () => {
      enforce(values.country).message('Country is required').isNotEmpty();
    });
  });

  const isCanada = values.country === CANADA;

  omitWhen(!formFields?.capture_address, () => {
    test('addressLine1', () => {
      enforce(values.addressLine1).message('Street address is required').isNotEmpty().isNotBlank();
      enforce(values.addressLine1?.length)
        .message(`Max character limit: ${ADDRESS_LINE_1_CHARACTER_LIMIT}`)
        .lessThan(ADDRESS_LINE_1_CHARACTER_LIMIT)
        .isNotBlank();
    });
  });

  omitWhen(!formFields?.capture_address || !values.addressLine2, () => {
    test('addressLine2', () => {
      enforce(values.addressLine2?.length)
        .message(`Max character limit: ${ADDRESS_LINE_2_CHARACTER_LIMIT}`)
        .lessThan(ADDRESS_LINE_2_CHARACTER_LIMIT)
        .isNotBlank();
    });
  });

  omitWhen(!formFields?.capture_address, () => {
    test('state', () => {
      enforce(values.state)
        .message(`${isCanada ? 'Province' : 'State'} is required`)
        .isNotEmpty();
      enforce(values.state)
        .message(`Please use ${isCanada ? 'province' : 'state'} abbreviation`)
        .lengthEquals(2);
    });
  });

  omitWhen(!formFields?.capture_address, () => {
    test('city', () => {
      enforce(values.city).message('City is required').isNotEmpty().isNotBlank();
      enforce(values.city?.length)
        .message(`Max character limit: ${CITY_CHARACTER_LIMIT}`)
        .lessThan(CITY_CHARACTER_LIMIT);
    });
  });

  omitWhen(!formFields?.capture_address, () => {
    test('zipCode', () => {
      enforce(values.zipCode)
        .message(`${isCanada ? 'Postal' : 'Zip'} code is required`)
        .isNotEmpty();
      enforce(values.zipCode)
        .message(`${isCanada ? 'Postal' : 'Zip'} code is not valid`)
        .condition((value) => (values.country === 'Canada' ? caZipCodeRegExp : usZipCodeRegExp).test(value));
    });
  });

  omitWhen(!formFields?.capture_service_details, () => {
    test('capture_service_details', () => {
      enforce(values?.capture_service_details).message('Service details is required').isNotEmpty().isNotBlank();
      enforce(values?.capture_service_details?.length)
        .message(`Max character limit: ${CHARACTER_LIMIT}`)
        .lessThan(CHARACTER_LIMIT);
    });
  });

  omitWhen(!formFields?.capture_how_did_you_hear_about_us, () => {
    test('capture_how_did_you_hear_about_us', () => {
      enforce(values.capture_how_did_you_hear_about_us)
        .message('How did you hear about us is required')
        .isNotEmpty()
        .isNotBlank();
      enforce(values.capture_how_did_you_hear_about_us?.length)
        .message(`Max character limit: ${CHARACTER_LIMIT}`)
        .lessThan(CHARACTER_LIMIT);
    });
  });

  omitWhen(!formFields?.capture_frequency, () => {
    test('capture_frequency', () => {
      enforce(values.capture_frequency).message('Frequency is required').isNotEmpty().isNotBlank();
      enforce(values.capture_frequency?.length)
        .message(`Max character limit: ${CHARACTER_LIMIT}`)
        .lessThan(CHARACTER_LIMIT);
    });
  });

  omitWhen(!formFields?.capture_custom_question, () => {
    test('capture_custom_question', () => {
      enforce(values.capture_custom_question)
        .message(`${formFields?.custom_question_label} is required`)
        .isNotEmpty()
        .isNotBlank();
      enforce(values.capture_custom_question?.length)
        .message(`Max character limit: ${CHARACTER_LIMIT}`)
        .lessThan(CHARACTER_LIMIT);
    });
  });
});

export const onValidate = (formFields?: Partial<LeadCaptureFormFields>) => (values: LeadFormValues) => {
  const { getErrors } = suite(values, formFields);
  const errors = getErrors();
  return errors;
};
